import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Stack, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";

import { formatShortDate } from "../../../../../../../../format/formatDate";
import { formatEuro } from "../../../../../../../../format/formatNumber";
import { DataTable } from "../../../../../../../../table/DataTable";
import { createColumns } from "../../../../../../../../table/DataTable.utils";
import { OwnClaimFormValue } from "../../../../OwnClaimsView.types";

const SECTION = "clients.client.debtSettlement.assetInventory.ownClaimsTab.list";

const CELL_SX = { whiteSpace: "pre-line", overflowWrap: "anywhere" } as const;

const HEADER_HEIGHT = 56;

interface Props {
  claims: OwnClaimFormValue[];
  incompleteKeys: ReadonlySet<string>;
  onEdit: (key: string) => void;
}

export function OwnClaimsTable({ claims, incompleteKeys, onEdit }: Props) {
  const { t, i18n } = useTranslation();

  const cell = (renderClaim: (claim: OwnClaimFormValue) => ReactNode, alignEnd = false) =>
    function OwnClaimCell({ row }: GridRenderCellParams<OwnClaimFormValue>) {
      return (
        <Stack gap={0.25} alignItems={alignEnd ? "flex-end" : "flex-start"} sx={CELL_SX}>
          {renderClaim(row)}
        </Stack>
      );
    };

  const columns = createColumns<OwnClaimFormValue>([
    {
      field: "debtorName",
      headerName: t(`${SECTION}.debtor`),
      flex: 1.4,
      sortable: false,
      renderCell: cell((claim) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {claim.debtorName}
          </Typography>
          <Typography variant="body2">{claim.debtorAddress}</Typography>
          {claim.isRelated === false ? t(`${SECTION}.notRelated`) : claim.relationship}
          {incompleteKeys.has(claim.key) && (
            <Typography variant="body2" color="error.main">
              {t("clients.client.debtSettlement.assetInventory.sectionIncomplete")}
            </Typography>
          )}
        </>
      )),
    },
    {
      field: "legalReason",
      headerName: t(`${SECTION}.legalReason`),
      flex: 1,
      sortable: false,
      renderCell: cell((claim) => claim.legalReason),
    },
    {
      field: "amount",
      headerName: t(`${SECTION}.amountAndDueDate`),
      flex: 0.9,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: cell(
        (claim) => (
          <>
            {claim.amount !== null && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatEuro(claim.amount, i18n.language)}
              </Typography>
            )}
            {claim.dueDate !== null && <Typography variant="body2">{formatShortDate(claim.dueDate)}</Typography>}
          </>
        ),
        true
      ),
    },
    {
      field: "securities",
      headerName: t(`${SECTION}.securities`),
      flex: 1,
      sortable: false,
      renderCell: cell((claim) => (claim.isSecured === false ? t(`${SECTION}.noSecurities`) : claim.securities)),
    },
    {
      field: "disputeDetails",
      headerName: t(`${SECTION}.disputed`),
      flex: 1,
      sortable: false,
      renderCell: cell((claim) => (claim.isDisputed === false ? t(`${SECTION}.notDisputed`) : claim.disputeDetails)),
    },
    {
      field: "collectionSteps",
      headerName: t(`${SECTION}.collectible`),
      flex: 1,
      sortable: false,
      renderCell: cell((claim) =>
        claim.isCollectible === true ? t(`${SECTION}.isCollectible`) : claim.collectionSteps
      ),
    },
  ]);

  return (
    <DataTable
      data={claims}
      columns={columns}
      getRowId={(claim) => claim.key}
      onRowClick={onEdit}
      autoRowHeight
      autoHeight
      columnHeaderHeight={HEADER_HEIGHT}
      disableColumnMenu
      hideNoRowsOverlay
    />
  );
}
